import { useState, useEffect } from 'react';
import api from './api';

const ROLE_LABELS = {
  admin: '管理员',
  sales: '预混料销售部',
  procurement: '牛场采购',
  warehouse: '牛场库管',
  factory: '代工厂',
};

const ROLE_COLORS = {
  admin: '#ef4444',
  sales: '#f59e0b',
  procurement: '#3b82f6',
  warehouse: '#10b981',
  factory: '#a78bfa',
};

export default function AdminTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div style={s.loading}>加载中...</div>;

  return (
    <div>
      <div style={s.header}>
        <h2 style={s.title}>用户管理</h2>
        <button style={s.btnPrimary} onClick={() => setShowAdd(true)}>+ 新建用户</button>
      </div>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>{['用户名', '姓名', '角色', '权限说明', '创建时间'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={s.tr}>
                <td style={s.td}><code style={s.code}>{u.username}</code></td>
                <td style={s.td}>{u.name}</td>
                <td style={s.td}>
                  <span style={{ ...s.roleBadge, background: ROLE_COLORS[u.role] + '22', color: ROLE_COLORS[u.role], border: `1px solid ${ROLE_COLORS[u.role]}44` }}>
                    {ROLE_LABELS[u.role]}
                  </span>
                </td>
                <td style={s.td}><span style={s.perm}>{getPermDesc(u.role)}</span></td>
                <td style={s.td}>{u.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={s.legend}>
        <div style={s.legendTitle}>角色权限说明</div>
        <div style={s.legendGrid}>
          {Object.entries(ROLE_LABELS).map(([role, label]) => (
            <div key={role} style={s.legendCard}>
              <span style={{ ...s.roleBadge, background: ROLE_COLORS[role] + '22', color: ROLE_COLORS[role] }}>{label}</span>
              <div style={s.legendDesc}>{getPermDesc(role)}</div>
            </div>
          ))}
        </div>
      </div>

      {showAdd && (
        <AddUserModal onClose={() => setShowAdd(false)}
          onSave={async data => { await api.post('/users', data); setShowAdd(false); load(); }} />
      )}
    </div>
  );
}

function getPermDesc(role) {
  const map = {
    admin: '全部操作权限',
    sales: '录入预混料发货记录',
    procurement: '新建计划单、录入到货、新建配方',
    warehouse: '录入成品到货、预混料入库复核',
    factory: '确认预混料到货、查看库存',
  };
  return map[role] || '—';
}

function AddUserModal({ onClose, onSave }) {
  const [form, setForm] = useState({ username: '', password: '', name: '', role: 'sales' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.username || !form.password || !form.name) { setError('请填写所有字段'); return; }
    setSaving(true);
    try { await onSave(form); }
    catch (e) { setError(e.response?.data?.error || '保存失败'); }
    finally { setSaving(false); }
  };

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <div style={s.modalHeader}>
          <span style={s.modalTitle}>新建用户</span>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.formGrid}>
          <label style={s.label}>用户名</label>
          <input style={s.input} placeholder="登录用户名" value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} />
          <label style={s.label}>姓名</label>
          <input style={s.input} placeholder="显示名称" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          <label style={s.label}>密码</label>
          <input style={s.input} type="password" placeholder="初始密码" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
          <label style={s.label}>角色</label>
          <select style={s.input} value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
            {Object.entries(ROLE_LABELS).filter(([r]) => r !== 'admin').map(([r, l]) => <option key={r} value={r}>{l}</option>)}
          </select>
        </div>
        {error && <div style={s.error}>{error}</div>}
        <div style={s.modalFooter}>
          <button style={s.btnSec} onClick={onClose}>取消</button>
          <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? '保存中...' : '创建用户'}</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  loading: { color: '#475569', padding: 40, textAlign: 'center' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  tableWrap: { overflowX: 'auto', borderRadius: 10, border: '1px solid #1e2d4a', marginBottom: 28 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#0d1529', color: '#64748b', padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 12, letterSpacing: '0.06em', borderBottom: '1px solid #1e2d4a' },
  tr: { borderBottom: '1px solid #1a2540' },
  td: { padding: '12px 14px', color: '#cbd5e1' },
  code: { background: '#0a1628', color: '#38bdf8', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' },
  roleBadge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  perm: { color: '#475569', fontSize: 12 },
  legend: { background: '#0d1529', border: '1px solid #1e2d4a', borderRadius: 12, padding: 20 },
  legendTitle: { fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 14, letterSpacing: '0.05em' },
  legendGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 },
  legendCard: { background: '#0a1628', borderRadius: 8, padding: 12 },
  legendDesc: { fontSize: 12, color: '#475569', marginTop: 6 },
  btnPrimary: { background: '#1d4ed8', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  btnSec: { background: '#1e2d4a', color: '#94a3b8', border: '1px solid #2d3f5f', padding: '8px 18px', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: '#0d1529', border: '1px solid #1e2d4a', borderRadius: 14, width: 'min(480px,95vw)', maxHeight: '85vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #1e2d4a' },
  modalTitle: { fontSize: 15, fontWeight: 700, color: '#f1f5f9' },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', fontSize: 18, cursor: 'pointer' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid #1e2d4a' },
  formGrid: { display: 'grid', gridTemplateColumns: '80px 1fr', gap: '10px 16px', padding: '20px 24px', alignItems: 'center' },
  label: { fontSize: 12, color: '#64748b', fontWeight: 600, textAlign: 'right' },
  input: { background: '#0a1628', border: '1px solid #2d3f5f', color: '#e2e8f0', padding: '8px 12px', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  error: { color: '#f87171', fontSize: 13, margin: '0 24px 12px', textAlign: 'center' },
};
